/**
 * `/`: "Démarrer" — the session feature's home screen. Renders under
 * `RequireFullAccess` inside `AppShell` (`src/pages/HomePage.tsx` mounts it).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listSessions } from '../../../api/sessions';
import { Button, Card, EmptyState, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { SessionCard } from '../components/SessionCard';
import { sessionErrorMessage } from '../errors';
import { useOptionalPosition } from '../hooks/useOptionalPosition';

import type { SessionWithExtras } from '../../../api/types';

/**
 * Render the "Démarrer" screen: open a session, join one already running,
 * or jump to the volunteer's own follow-ups.
 *
 * @returns The page element.
 */
export function StartPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { position, granted } = useOptionalPosition();
  const [sessions, setSessions] = useState<SessionWithExtras[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listSessions({ state: 'open', ...(granted && position ? { near: position } : {}) })
      .then((page) => {
        if (!cancelled) {
          setSessions(page.results);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSessions([]);
          showToast(sessionErrorMessage(error) || t('session.start.loadError'), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [granted, position, showToast]);

  return (
    <div className="pile">
      <Card>
        <h1>{t('session.start.title')}</h1>
        <div className="pile">
          <Button variant="primary" fullWidth onClick={() => navigate('/sessions/new')}>
            {t('session.start.startButton')}
          </Button>
          <p className="muted" style={{ margin: 0 }}>
            {t('session.start.startHint')}
          </p>
        </div>
      </Card>

      <Card>
        <p className="kicker">{t('session.start.openSessionsKicker')}</p>
        {loading ? (
          <div className="pile" aria-hidden="true">
            <Skeleton height="4.5rem" />
            <Skeleton height="4.5rem" />
          </div>
        ) : sessions && sessions.length > 0 ? (
          <>
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onSelect={() => navigate('/sessions/join')}
              />
            ))}
            <Button variant="secondary" fullWidth onClick={() => navigate('/sessions/join')}>
              {t('session.start.seeAllSessions')}
            </Button>
          </>
        ) : (
          <EmptyState
            title={t('session.start.openSessionsKicker')}
            body={t('session.start.openSessionsEmpty')}
          />
        )}
      </Card>

      <Card>
        <p className="kicker">{t('session.start.myCasesKicker')}</p>
        <Card
          className="ui-card--highlight"
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/cases')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              navigate('/cases');
            }
          }}
        >
          <p style={{ margin: 0 }}>{t('session.start.myCasesCta')}</p>
        </Card>
      </Card>
    </div>
  );
}
